import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { FilePicker } from "./FilePicker";
import { DropdownField } from "../../../components/dropdown/DropdownField";
import ApiService from "../../../services/ApiService";
import {
  ShowErrorToast,
  ShowSuccessToast,
} from "../../../components/toaster/toast";
import { useNavigate } from "react-router-dom";
import { DotLoaderComp } from "../../../components/DotLoader/index";

export const CreatePlanModule = () => {
  const [loading, setLoading] = useState(false);
  const [premiumFiles, setPremiumFiles] = useState([]);
  const navigate = useNavigate();

  // Mapping from file names to region_id
  const regionMapping = {
    Europe: 1,
    "Others WW": 2,
    "Singapore & Hongkong": 3,
    "North America": 4,
    TAL: 5,
    "All First Care": 7,
  };

  const areaOptions = [
    { value: 3, label: "Worldwide" },
    { value: 4, label: "ASEAN Excluding Singapore & Hong Kong" },
    {
      value: 5,
      label:
        "Worldwide Ex. N/S America, CN, HK, TW, JP, SG, IL, UAE, RU, CH, UK.",
    },
  ];

  const planTypeOptions = [
    { value: "inpatient", label: "Inpatient" },
    { value: "outpatient", label: "Outpatient" },
  ];

    const coverOptions = [
      { value: "Life Care", label: "Life Care" },
      { value: "First Care", label: "First Care" },
    ];

  const shortBenefitOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "Optional", label: "Optional" },
  ];

  const formik = useFormik({
    initialValues: {
      name: "",
      annual_limit: "",
      type: "inpatient",
      cover: "Life Care",
      area_id: 3,
      shortBenefits: {
        evacuation_and_repatriation: "yes",
        inpatient_benefits: "yes",
        maternity: "yes",
        free_newborn_cover: "yes",
        outpatient_benefits: "yes",
        vision_benefit: "yes",
        dental_benefit: "yes",
        travel_benefit: "yes",
        medical_checkup_and_vaccinations: "yes",
      },
      detailed_benefits: null,
      premiums: [], // Each premium file: { fileName, file, region_id }
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Convert the detailed benefits file to Base64 (if provided)
        const detailedBenefitsBase64 = values.detailed_benefits
          ? await convertToBase64(values.detailed_benefits)
          : null;
        // Process premium files: convert each file to Base64 and include its region_id
        const premiumsProcessed =
          values.premiums && values.premiums.length > 0
            ? await Promise.all(
                values.premiums.map(async (premium) => ({
                  fileName: premium.fileName,
                  content: await convertToBase64(premium.file),
                  region_id: premium.region_id,
                  // area_id: formik.values.area_id, // Include the area id here
                }))
              )
            : [];

        const payload = {
          ...values,
          // Wrap detailed benefits in an object with a "content" property
          detailed_benefits: detailedBenefitsBase64
            ? { content: detailedBenefitsBase64 }
            : null,
          premiums: premiumsProcessed,
        };

        const response = await ApiService.createModule(payload);
        console.log(response.data);
        ShowSuccessToast("Plan created successfully");
        navigate('/plans');
      } catch (error) {
        ShowErrorToast("Failed to create plan");
      } finally {
        setLoading(false);
      }
    },
  });

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  // Update premium file list based on area_id
  useEffect(() => {
    const fetchPremiumFiles = () => {
      let files = [];
      if (formik.values.area_id === 3) {
        files = [
          "Europe",
          "Others WW",
          "Singapore & Hongkong",
          "North America",
        ];
      } else if (formik.values.area_id === 4) {
        files = ["TAL"];
      } else if (formik.values.area_id === 5) {
        files = ["All First Care"];
      }
      setPremiumFiles(files);
      formik.setFieldValue("premiums", []); // Reset the premium files
    };
    fetchPremiumFiles();
  }, [formik.values.area_id]);

    // Filter the area options based on the selected cover type
    const filteredAreaOptions =
    formik.values.cover === "Life Care"
      ? areaOptions.filter((option) => option.value === 3 || option.value === 4)
      : // Assuming "life_fire_care" (first care) should only allow option 5
        areaOptions.filter((option) => option.value === 5);

  // Reset area_id if the current selection isn't valid for the selected cover type
  useEffect(() => {
    if (!filteredAreaOptions.some((option) => option.value === formik.values.area_id)) {
      formik.setFieldValue("area_id", filteredAreaOptions[0].value);
    }
  }, [formik.values.cover, filteredAreaOptions]);

  return (
    <div className="bg-[#F9F8F5] px-5 py-7 ">
      <h1 className="text-[#012949] font-semibold text-[24px] mb-4">
        Create a Plan here with Premiums and Benefits
      </h1>
      <div className="bg-white px-5 py-7 rounded-lg min-h-[50vh]">
        {/* {loading ? <DotLoaderComp /> : ( */}
        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
            <div>
              <label
                htmlFor="name"
                className="block mb-0 text-[14px] font-semibold"
              >
                Plan Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Plan Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                className="border p-2 rounded w-full"
              />
            </div>
            <div>
              <label
                htmlFor="annual_limit"
                className="block mb-0 text-[14px] font-semibold"
              >
                Annual Limit
              </label>
              <input
                type="number"
                id="annual_limit"
                name="annual_limit"
                placeholder="Annual Limit"
                value={formik.values.annual_limit}
                onChange={formik.handleChange}
                className="border p-2 rounded w-full"
              />
            </div>
            <DropdownField
              label="Plan Type"
              options={planTypeOptions}
              value={formik.values.type}
              onChange={(e) => formik.setFieldValue("type", e.target.value)}
            />
            <DropdownField
              label="Cover"
              options={coverOptions}
              value={formik.values.cover}
              onChange={(e) => formik.setFieldValue("cover", e.target.value)}
            />
            <DropdownField
              label="Area of Cover"
              options={filteredAreaOptions}
              value={formik.values.area_id}
              onChange={(e) =>
                formik.setFieldValue("area_id", Number(e.target.value))
              }
            />
          </div>

          <h2 className="text-lg font-medium mb-4">Short Benefits</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {Object.keys(formik.values.shortBenefits).map((key) => (
              <DropdownField
                key={key}
                label={key.replace("_", " ")}
                options={shortBenefitOptions}
                value={formik.values.shortBenefits[key]}
                onChange={(e) =>
                  formik.setFieldValue(`shortBenefits.${key}`, e.target.value)
                }
              />
            ))}
          </div>

          <h2 className="text-lg font-medium mt-6 mb-4">Detailed Benefits</h2>
          {/* FilePicker for detailed benefits CSV; expects a header row */}
          <FilePicker
            title="Upload Detailed Benefits CSV"
            fileType="benefits"
            onChange={(e) =>
              formik.setFieldValue("detailed_benefits", e.target.files[0])
            }
          />

          <h2 className="text-lg font-medium mt-6 mb-4">Premiums</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {premiumFiles.map((fileName) => (
              // FilePicker for premium CSV; expects a two-column numeric CSV without headers
              <FilePicker
                key={fileName}
                title={`Upload ${fileName} CSV`}
                fileType="premiums"
                onChange={(e) => {
                  // Append the validated file along with its region_id
                  formik.setFieldValue("premiums", [
                    ...formik.values.premiums,
                    {
                      fileName,
                      file: e.target.files[0],
                      region_id: regionMapping[fileName], // Added region_id mapping here
                    },
                  ]);
                }}
              />
            ))}
          </div>
          <div className="pt-6">
            <button
              type="submit"
              className="w-[160px] h-[46px] bg-orangeColor rounded-[10px] font-inter text-base font-bold text-white"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Module"}
            </button>
          </div>
        </form>
        {/* )} */}
      </div>
    </div>
  );
};
